/**
 * BrandPage Component
 * Displays products for a specific brand with filtering and sorting
 * Uses infinite scroll to load more products as user scrolls
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Dropdown } from 'react-bootstrap';
import { useBrands, useInfiniteProducts } from '../../hooks/useProducts.js';
import { useMedRecFilter } from '../../context/MedRecFilterContext.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import ProductCard from '../../components/products/ProductCard.jsx';
import { imageHelpers } from '../../services/api.js';
import { BRAND_PAGE_PRODUCT_SORT } from '../../constants/productSort.js';

const BrandPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get Med/Rec filter from global context
  const { filterValues } = useMedRecFilter();

  // Get URL parameters
  const initialSort = searchParams.get('sort') || BRAND_PAGE_PRODUCT_SORT;
  const initialOrder = searchParams.get('order') || 'asc';
  const initialInStock = searchParams.get('inStock') === 'true';

  // Local state
  const [sortBy, setSortBy] = useState(initialSort);
  const [sortDirection, setSortDirection] = useState(initialOrder);
  const [inStockOnly, setInStockOnly] = useState(initialInStock);

  // Infinite scroll sentinel ref
  const sentinelRef = useRef(null);

  // Find brand by slug first to get ID
  const { data: brands = [] } = useBrands({ pageSize: 100 });
  const brand = brands.find(b => b.slug === slug);
  const brandId = brand?.id;

  const brandImageUrls = imageHelpers.getBrandImageUrl(brand);

  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);

  const handleImageError = () => {
    if (currentUrlIndex < brandImageUrls.length - 1) {
      setCurrentUrlIndex(currentUrlIndex + 1);
    }
  };

  // Get products for this brand with infinite scroll
  const {
    products,
    pagination,
    isLoading: productsLoading,
    error: productsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchProducts
  } = useInfiniteProducts({
    brandId,
    pageSize: 24,
    sortBy,
    sortDirection,
    inStock: inStockOnly,
    isMed: filterValues.isMed,
    isRec: filterValues.isRec,
    enabled: !!brandId,
  });

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '600px',
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (sortBy !== BRAND_PAGE_PRODUCT_SORT) params.set('sort', sortBy);
    if (sortDirection !== 'asc') params.set('order', sortDirection);
    if (inStockOnly) params.set('inStock', 'true');

    setSearchParams(params);
  }, [sortBy, sortDirection, inStockOnly, setSearchParams]);

  // Handle sort change
  const handleSortChange = (newSortBy, newDirection = 'asc') => {
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'inStock':
        setInStockOnly(value);
        break;
      default:
        break;
    }
  };

  // Loading states
  if (!brands.length) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="container-xxl py-5">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-3 text-muted">Loading brand...</p>
          </div>
        </Container>
      </div>
    );
  }

  // Error states - brand not found by slug
  if (!brand) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="container-xxl py-5">
          <div className="text-center">
            <div className="alert alert-danger">
              <h4>Brand Not Found</h4>
              <p>The brand "{slug}" could not be found.</p>
              <Link to="/brands" className="btn btn-primary">
                View All Brands
              </Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      {/* Brand Header */}
      <section className="bg-white py-5 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center">
            <Col md={8}>
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb justify-content-center justify-content-md-start mb-4 mb-md 0">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-decoration-none">Home</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/brands" className="text-decoration-none">Brands</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {brand.name}
                  </li>
                </ol>
              </nav>
              <Row className="justify-content-center justify-content-md-start">
                <Col xs={9} md={3}>
                  <img
                    src={brandImageUrls[currentUrlIndex]}
                    alt={`${brand.name} logo`}
                    className="brand-card__logo img-fluid mb-5 mb-md-0"
                    onError={handleImageError}
                    loading="lazy"
                  />
                </Col>
                <Col xs={9} md={6} className="mb-5 mb-md-0">
                  <h1 className="h2 mb-2">{brand.name}</h1>
                  {brand.description && (
                    <p className="text-muted mb-2 brand-description">
                      {brand.description}
                    </p>
                  )}
                  <p className="text-stash-accent mb-0">
                    <strong>{pagination.totalItems || 0}</strong> products available
                  </p>
                </Col>
              </Row>
            </Col>

            <Col md={4} className="text-end">
              <Link to="/brands" className="btn btn-outline-primary">
                <i className="bi bi-arrow-left me-1"></i>
                All Brands
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filters and Controls */}
      <section className="bg-white py-3 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center">
            <Col md={6}>
              {/* Filters */}
              <div className="d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id="inStockFilter"
                  label="In Stock Only"
                  checked={inStockOnly}
                  onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                  className="me-4"
                />
              </div>
            </Col>

            <Col md={6}>
              {/* Sort Controls */}
              <div className="d-flex align-items-center justify-content-md-end">
                <span className="me-3 text-muted">Sort by:</span>

                <Dropdown align="end">
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    {sortBy.includes('productSectionDisplayOrder') && 'Default'}
                    {sortBy === 'name' && 'Name'}
                    {sortBy === 'price' && 'Price'}
                    {sortBy === 'newest' && 'Newest'}
                    <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleSortChange(BRAND_PAGE_PRODUCT_SORT, 'asc')}>
                      Default
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handleSortChange('name', 'asc')}>
                      Name (A-Z)
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('name', 'desc')}>
                      Name (Z-A)
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handleSortChange('price', 'asc')}>
                      Price (Low to High)
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('price', 'desc')}>
                      Price (High to Low)
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handleSortChange('newest', 'desc')}>
                      Newest First
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Products Grid */}
      <section className="py-4">
        <Container fluid className="">
          {productsLoading ? (
            <div className="text-center py-5">
              <LoadingSpinner />
              <p className="mt-3 text-muted">Loading products...</p>
            </div>
          ) : productsError ? (
            <div className="text-center py-5">
              <div className="alert alert-warning">
                <h5>Error Loading Products</h5>
                <p>{productsError.message}</p>
                <Button variant="primary" onClick={() => refetchProducts()}>
                  Try Again
                </Button>
              </div>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="product-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="text-center py-4">
                {isFetchingNextPage && (
                  <>
                    <LoadingSpinner />
                    <p className="mt-2 text-muted">Loading more products...</p>
                  </>
                )}
                {!hasNextPage && products.length > 24 && (
                  <small className="text-muted">
                    Showing all {pagination.totalItems} products
                  </small>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-box-seam display-1 text-muted mb-3"></i>
              <h3 className="text-muted">No Products Found</h3>
              <p className="text-muted">
                {inStockOnly
                  ? `No products from ${brand.name} are currently in stock.`
                  : `${brand.name} doesn't have any products available yet.`
                }
              </p>
              {inStockOnly && (
                <Button
                  variant="outline-primary"
                  onClick={() => handleFilterChange('inStock', false)}
                >
                  Show All Products
                </Button>
              )}
            </div>
          )}
        </Container>
      </section>

      <style>{`
        .brand-description {
          max-width: 600px;
          line-height: 1.5;
        }

        @media (max-width: 767.98px) {
          .brand-description {
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};

export default BrandPage;